/**
 * Run competitor syncs one-by-one so the UI can show real progress (count / total).
 * Pauses between calls to reduce Gemini / Google rate limits.
 */

export function delay(ms) {
  if (ms == null || ms <= 0) return Promise.resolve();
  return new Promise((r) => setTimeout(r, ms));
}

const RETRYABLE_STATUS = new Set([429, 503, 504]);

function extractApiError(err) {
  return (
    err?.userMessage
    || err?.response?.data?.error
    || err?.message
    || 'Sync failed'
  );
}

function isRetryableError(err) {
  const status = err?.response?.status ?? err?.statusCode;
  return RETRYABLE_STATUS.has(Number(status));
}

async function execWithRetry(exec, row, { maxRetries = 2, retryDelayMs = 30000 } = {}) {
  let lastError = null;
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      await exec(row);
      return { ok: true, error: null };
    } catch (err) {
      lastError = extractApiError(err);
      if (attempt < maxRetries && isRetryableError(err)) {
        await delay(retryDelayMs * (attempt + 1));
        continue;
      }
      return { ok: false, error: lastError };
    }
  }
  return { ok: false, error: lastError || 'Sync failed' };
}

/**
 * @param {Array<{ id: unknown, name?: string }>} rows
 * @param {(row: { id: unknown, name?: string }) => Promise<void>} exec
 * @param {{ delayMs?: number, onProgress?: (args: { index: number, total: number, row: object }) => void }} [options]
 * @returns {Promise<{ successCount: number, failCount: number, total: number, failures: Array<{ id: unknown, name?: string, error: string }> }>}
 */
export async function runSequentialCompetitorSync(rows, exec, options = {}) {
  const { delayMs = 20000, onProgress } = options;
  const list = (rows || []).filter((r) => r != null && r.id != null);
  let successCount = 0;
  let failCount = 0;
  const failures = [];

  for (let i = 0; i < list.length; i += 1) {
    const row = list[i];
    onProgress?.({ index: i + 1, total: list.length, row });

    const result = await execWithRetry(exec, row);
    if (result.ok) {
      successCount += 1;
    } else {
      failCount += 1;
      failures.push({
        id: row.id,
        name: row.name,
        error: result.error || 'Sync failed',
      });
    }

    if (i < list.length - 1) await delay(delayMs);
  }

  return { successCount, failCount, total: list.length, failures };
}

export function formatSyncFailureHint(failures, maxItems = 2) {
  if (!failures?.length) return '';
  const sample = failures
    .slice(0, maxItems)
    .map((f) => `${f.name || f.id}: ${f.error}`)
    .join(' · ');
  const more = failures.length > maxItems ? ` (+${failures.length - maxItems} more)` : '';
  return ` Errors: ${sample}${more}`;
}
